// ── Filiais: regras puras (sem I/O) ───────────────────────────────
// A tabela é public.empresa_filiais. O banco repete as travas de CNPJ; aqui
// elas existem para a tela dizer o motivo antes do Salvar.

import type { EmpresaFilial, SalvarFilialInput } from './ports/IEmpresaRepository';

/** Só [0-9A-Z]. Tirar as letras corromperia o CNPJ alfanumérico (Receita,
 *  2026), que o Hub já aceita. Espelha public.cnpj_normalizado. */
export const normalizarCnpj = (valor: unknown): string =>
  String(valor ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');

/**
 * Dígito verificador do CNPJ, numérico ou alfanumérico. No alfanumérico cada
 * caractere vale o código ASCII menos 48 ('0' = 0 … 'A' = 17); para CNPJ só
 * de dígitos é exatamente o cálculo de sempre. Os dois DV são sempre dígitos.
 */
export const cnpjValido = (valor: unknown): boolean => {
  const cnpj = normalizarCnpj(valor);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) return false;
  if (/^(.)\1{13}$/.test(cnpj)) return false;
  const dv = (base: string): number => {
    let peso = base.length - 7;
    let soma = 0;
    for (const ch of base) {
      soma += (ch.charCodeAt(0) - 48) * peso;
      peso = peso === 2 ? 9 : peso - 1;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const d1 = dv(cnpj.slice(0, 12));
  const d2 = dv(cnpj.slice(0, 12) + d1);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
};

/** 00.000.000/0000-00, com letras onde houver. Incompleto volta como veio. */
export const formatarCnpj = (valor: unknown): string => {
  const c = normalizarCnpj(valor);
  if (c.length !== 14) return String(valor ?? '');
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
};

/** Mesma régua do trigger de public.empresa_filiais para `email_cobranca`. */
const EMAIL_BASICO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * O motivo de a filial não poder ser salva, ou null. `cnpjsMatriz` e
 * `outrasFiliais` são do mesmo grupo; a colisão com OUTRA empresa do escopo
 * só o banco conhece, e a mensagem dele chega na tela como está.
 *
 * `cnpjsMatriz` é lista porque o CNPJ da matriz mora em dois lugares que não
 * se sincronizam — o cadastro da empresa e o contrato de formalização — e o
 * banco confere os dois.
 */
export const motivoFilialInvalida = (
  input: SalvarFilialInput,
  contexto: { cnpjsMatriz: (string | null | undefined)[]; outrasFiliais: Pick<EmpresaFilial, 'id' | 'cnpj'>[]; filialId?: string },
): string | null => {
  const cnpj = normalizarCnpj(input.cnpj);
  if (!cnpj) return 'Informe o CNPJ da filial.';
  if (!cnpjValido(cnpj)) return 'CNPJ inválido: o dígito verificador não confere.';
  if (!input.razaoSocial.trim()) return 'Informe a razão social da filial.';
  const emailCobranca = String(input.emailCobranca ?? '').trim();
  if (!emailCobranca) return 'Informe o e-mail de cobrança da filial — é para onde vai o boleto.';
  if (!EMAIL_BASICO.test(emailCobranca)) return 'E-mail de cobrança da filial inválido.';
  if (contexto.cnpjsMatriz.some(m => m && cnpj === normalizarCnpj(m))) return 'Este CNPJ é o da matriz.';
  if (contexto.outrasFiliais.some(f => f.id !== contexto.filialId && f.cnpj === cnpj)) return 'Este CNPJ já está no grupo.';
  return null;
};

/** O que vai para o banco. Sem `dadosProprios` o bloco vai vazio: guardar um
 *  bloco que não vale só serviria para, um dia, alguém lê-lo por engano. */
export const payloadDaFilial = (input: SalvarFilialInput): Required<SalvarFilialInput> => {
  const t = (v: unknown) => String(v ?? '').trim();
  const proprio = input.dadosProprios;
  return {
    cnpj: normalizarCnpj(input.cnpj),
    razaoSocial: t(input.razaoSocial),
    // Fora do bloco: valem com ou sem `dadosProprios`.
    nomeFantasia: t(input.nomeFantasia),
    emailCobranca: t(input.emailCobranca).toLowerCase(),
    dadosProprios: proprio,
    endereco: proprio ? t(input.endereco) : '',
    email: proprio ? t(input.email).toLowerCase() : '',
    representanteNome: proprio ? t(input.representanteNome) : '',
    representanteCpf: proprio ? t(input.representanteCpf).replace(/\D/g, '') : '',
    emailAssinatura: proprio ? t(input.emailAssinatura).toLowerCase() : '',
  };
};
